import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { FiDownload, FiFileText, FiLoader, FiPaperclip, FiPlus, FiRotateCcw, FiSearch, FiX } from "react-icons/fi";
import ComponentLoader from "../components/ComponentLoader";
import ScreenHeader from "./ScreenHeader";
import {
  CommentComposer,
  mentionsFromRichText,
  ProjectCommentBody,
  ProjectRichText,
} from "./ProjectsScreen";
import ScreenMetric from "./ScreenMetric";

const isClosedStatus = (status = "") => ["closed", "resolved"].includes(String(status).trim().toLowerCase());

const requestStatusClass = (status = "") =>
  isClosedStatus(status) ? "approved" : String(status || "pending").toLowerCase().replace(/\s+/g, "-");

const REQUEST_STATUS_OPTIONS = ["Pending Approval", "Review", "Working", "Closed"];

const FINANCE_REQUEST_OWNER = { label: "Ashish Jangid", email: "ashish.jangid@antrazal.com" };
const HR_REQUEST_OWNER = { label: "Mayank Moolchandani", email: "mayank@antrazal.com" };
const REQUEST_OWNER_OPTIONS = [
  FINANCE_REQUEST_OWNER,
  HR_REQUEST_OWNER,
  { label: "Shiv Kumar Bagherwal", email: "shiv@antrazal.com" },
];

const normalizeEmail = (value = "") => String(value || "").trim().toLowerCase();
const normalizeText = (value = "") => String(value || "").trim().toLowerCase();
const canonicalCasePersonName = (name = "", email = "") => {
  const normalizedName = normalizeText(name);
  const shivAliases = ["hsiv sharma", "shiv bagherwal", "shiv kumar bagherwal", "shiv sharma"];
  if (normalizeEmail(email) === "shiv@antrazal.com" || shivAliases.includes(normalizedName)) {
    return "Shiv Kumar Bagherwal";
  }
  return String(name || "").trim();
};
const normalizeStatusKey = (status = "") => normalizeText(status) || "unknown";
const isIntegrationOwner = (name = "", email = "") => {
  const ownerName = normalizeText(name);
  const ownerEmail = normalizeEmail(email);
  return ownerName.includes("integration") || ownerEmail.includes("integration") || ownerName === "queue" || ownerName.includes("request queue");
};

const ownerForRequestType = (requestType = "", request = {}) => {
  const source = request || {};
  const normalizedType = normalizeText(requestType);
  const subjectText = normalizeText(source.subject);
  if (source.referralCandidateId || normalizedType.includes("referral") || subjectText.includes("referral")) {
    return HR_REQUEST_OWNER;
  }
  if (
    normalizedType.includes("asset") ||
    normalizedType.includes("finance") ||
    normalizedType.includes("payroll") ||
    normalizedType.includes("salary") ||
    normalizedType.includes("tax") ||
    normalizedType.includes("operation") ||
    normalizedType.includes("expense") ||
    normalizedType.includes("reimbursement")
  ) {
    return FINANCE_REQUEST_OWNER;
  }
  if (normalizedType.includes("policy") || normalizedType.includes("human resource") || normalizedType === "hr") {
    return HR_REQUEST_OWNER;
  }
  return HR_REQUEST_OWNER;
};

const requestOwnerOption = (request = {}) => {
  const source = request || {};
  if (source.assignedToName || source.assignedToEmail) {
    const fallback = ownerForRequestType(source.requestType, source);
    return {
      label: canonicalCasePersonName(source.assignedToName || fallback.label, source.assignedToEmail || fallback.email),
      email: source.assignedToEmail || fallback.email,
    };
  }
  if (source.ownerName && !isIntegrationOwner(source.ownerName, source.ownerEmail)) {
    const fallback = ownerForRequestType(source.requestType, source);
    return {
      label: canonicalCasePersonName(source.ownerName, source.ownerEmail || fallback.email),
      email: source.ownerEmail || fallback.email,
    };
  }
  return ownerForRequestType(source.requestType, source);
};

const normalizeFilterKey = (value = "") => normalizeText(value).replace(/\s+/g, "-") || "unknown";
const normalizeOwnerKey = (owner = {}) => normalizeEmail(owner.email) || normalizeFilterKey(owner.label);
const requestRequesterOption = (request = {}) => {
  const email = normalizeEmail(request.requesterEmail);
  const label = canonicalCasePersonName(request.requesterName, email) || email || "Employee";
  return {
    email,
    id: String(request.requesterId || "").trim(),
    label,
  };
};
const normalizeRequesterKey = (requester = {}) =>
  normalizeEmail(requester.email) || requester.id || normalizeFilterKey(requester.label);

const filterSummary = ({ allLabel, defaultLabel, defaultValues = [], options = [], selectedValues = [] }) => {
  const selectedSet = new Set(selectedValues);
  if (!selectedValues.length) {
    return "None selected";
  }
  if (options.length && options.every((option) => selectedSet.has(option.value))) {
    return allLabel;
  }
  const defaultSet = new Set(defaultValues);
  const matchesDefault =
    defaultValues.length === selectedValues.length &&
    selectedValues.every((value) => defaultSet.has(value));
  if (matchesDefault) {
    return defaultLabel;
  }
  if (selectedValues.length === 1) {
    return options.find((option) => selectedSet.has(option.value))?.label || "1 selected";
  }
  return `${selectedValues.length} selected`;
};

// Keep explicit choices through refreshes, but never count unavailable options.
const activeFilterKeys = (selected, defaults, available = defaults) =>
  selected === null ? defaults : available.filter((value) => selected.includes(value));

const toggleFilterKey = (selected, defaults, available, value) => {
  const base = activeFilterKeys(selected, defaults, available);
  const next = base.includes(value) ? base.filter((item) => item !== value) : [...base, value];
  return next.length === defaults.length && defaults.every((item) => next.includes(item)) ? null : next;
};

const MultiSelectDropdown = ({ filterId, isOpen, label, onOpenChange, options = [], selectedValues = [], summary, onToggle }) => {
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  const menuId = useId();
  const summaryId = useId();

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    const closeOnOutside = (event) => {
      // Let the next trigger switch menus on click. Collapsing an inline mobile
      // menu on pointerdown/focus would move that trigger before the click lands.
      const nextTrigger = event.target.closest?.(".new-hrm-request-filter-dropdown__trigger");
      if (nextTrigger && dropdownRef.current?.closest(".new-hrm-request-filter-bar")?.contains(nextTrigger)) {
        return;
      }
      if (!dropdownRef.current?.contains(event.target)) {
        onOpenChange("");
      }
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onOpenChange("");
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("pointerdown", closeOnOutside);
    document.addEventListener("focusin", closeOnOutside);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutside);
      document.removeEventListener("focusin", closeOnOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [isOpen, onOpenChange]);

  return (
    <div ref={dropdownRef} className={`new-hrm-request-filter-dropdown ${isOpen ? "new-hrm-request-filter-dropdown--open" : ""}`}>
      <button
        ref={triggerRef}
        aria-controls={isOpen ? menuId : undefined}
        aria-describedby={summaryId}
        aria-expanded={isOpen}
        aria-label={`${label} filter`}
        className="new-hrm-request-filter-dropdown__trigger"
        data-filter-id={filterId}
        onClick={() => onOpenChange(isOpen ? "" : filterId)}
        type="button"
      >
        <span>{label}</span>
        <strong id={summaryId} title={summary}>{summary}</strong>
      </button>
      {isOpen && (
        <div id={menuId} className="new-hrm-request-filter-dropdown__menu" role="group" aria-label={`${label} options`}>
          {options.map((option) => (
            <label key={option.value}>
              <input
                type="checkbox"
                checked={selectedValues.includes(option.value)}
                onChange={() => onToggle(option.value)}
              />
              <span>{option.label}</span>
            </label>
          ))}
          {!options.length && <span className="new-hrm-request-filter-dropdown__empty">No options available</span>}
        </div>
      )}
    </div>
  );
};

const isSalesforceId = (value = "") => /^[a-zA-Z0-9]{15,18}$/.test(String(value || "").trim());
const getInitials = (name = "") =>
  String(name || "AWC")
    .split(" ")
    .filter(Boolean)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

const RequestCommentBody = ({ people = [], comment }) => {
  const body = comment?.body || "";
  const mentions = comment?.mentions?.length ? comment.mentions : mentionsFromRichText(body, people);

  return (
    <section className="new-hrm-request-comment-body">
      <ProjectCommentBody value={body} mentions={mentions} attachments={[]} />
    </section>
  );
};

const requestFileKey = (file = {}, index = 0) =>
  String(file.id || file.fileId || file.contentDocumentId || file.fileName || file.name || index);

const RequestDetailSkeleton = () => (
  <div className="new-hrm-request-detail-skeleton" role="status" aria-label="Loading case details">
    <section className="new-hrm-request-detail-skeleton__hero" aria-hidden="true">
      <div><i /><i /><i /></div>
      <span />
      <small />
    </section>
    <div className="new-hrm-request-detail-skeleton__content" aria-hidden="true">
      <main>
        <section><i /><span /><span /><small /></section>
        <section><i /><span /><span /></section>
        <section><i /><span /><span /><span /></section>
      </main>
      <aside>{Array.from({ length: 6 }, (_, index) => <span key={index}><i /><small /></span>)}</aside>
    </div>
    <footer aria-hidden="true"><span /><i /></footer>
  </div>
);

const RequestDetailDrawer = ({ directoryPeople = [], detailLoading = false, onClose, onDownloadFile, onSubmitComment, onInlineImageUpload, onUpdateRequest, request }) => {
  const [status, setStatus] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [isAttachingFiles, setIsAttachingFiles] = useState(false);
  const [isSavingManagement, setIsSavingManagement] = useState(false);
  const [downloadingFileId, setDownloadingFileId] = useState("");
  const attachmentInputRef = useRef(null);
  const resolvedOwner = requestOwnerOption(request);

  useEffect(() => {
    setStatus(request?.status || "Pending Approval");
    setOwnerEmail(normalizeEmail(resolvedOwner.email));
    setIsAttachingFiles(false);
    setIsSavingManagement(false);
    setDownloadingFileId("");
  }, [request?.id, request?.ownerEmail, request?.assignedToEmail, request?.requestType, request?.status, resolvedOwner.email]);

  useEffect(() => {
    if (!request) {
      return undefined;
    }
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose?.();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose, request]);

  if (!request) {
    return null;
  }

  const handleAttachFiles = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) {
      return;
    }
    setIsAttachingFiles(true);
    try {
      const didSave = await onSubmitComment(request.id, "", files);
      if (didSave && attachmentInputRef.current) {
        attachmentInputRef.current.value = "";
      }
    } finally {
      setIsAttachingFiles(false);
    }
  };

  const handleDownloadFile = async (file, index) => {
    const fileId = requestFileKey(file, index);
    setDownloadingFileId(fileId);
    try {
      await onDownloadFile?.(file);
    } finally {
      setDownloadingFileId("");
    }
  };

  const canEditManagement = request.canReassign || request.canChangeStatus;
  const canAttachFiles = request.canComment !== false && typeof onSubmitComment === "function" && isSalesforceId(request.id);
  const currentOwnerEmail = normalizeEmail(resolvedOwner.email);
  const hasUnavailableCurrentOwner = Boolean(currentOwnerEmail) && !REQUEST_OWNER_OPTIONS.some((option) => option.email === currentOwnerEmail);
  const selectedOwnerAvailable = REQUEST_OWNER_OPTIONS.some((option) => option.email === ownerEmail);
  const ownerChanged = request.canReassign && ownerEmail && ownerEmail !== currentOwnerEmail;
  const statusChanged = request.canChangeStatus && status && status !== request.status;
  const hasManagementChanges = ownerChanged || statusChanged;

  const handleManageSave = async () => {
    if (!hasManagementChanges || !onUpdateRequest) {
      return;
    }
    setIsSavingManagement(true);
    try {
      await onUpdateRequest(request.id, {
        ...(statusChanged ? { status } : {}),
        ...(ownerChanged ? { ownerEmail } : {}),
      });
    } finally {
      setIsSavingManagement(false);
    }
  };

  return (
    <div className="new-hrm-project-drawer-backdrop new-hrm-request-drawer-backdrop" role="presentation" onClick={onClose}>
      <aside
        className="new-hrm-project-drawer new-hrm-request-drawer"
        role="dialog"
        aria-modal="true"
        aria-busy={detailLoading}
        aria-labelledby={detailLoading ? undefined : "request-detail-title"}
        aria-label={detailLoading ? `Loading case ${request.requestNumber}` : undefined}
        onClick={(event) => event.stopPropagation()}
      >
        <header className="new-hrm-project-drawer__header">
          <div className="new-hrm-project-drawer__utility-left">
            <button type="button" className="new-hrm-icon-button" onClick={onClose} aria-label="Close case detail" title="Close">
              <FiX aria-hidden="true" />
            </button>
            <div className="new-hrm-project-drawer__breadcrumb">
              <span className="new-hrm-request-drawer__workspace">HRM Cases</span>
              <span className="new-hrm-project-drawer__separator" aria-hidden="true">/</span>
              <strong title={request.requestNumber}>{request.requestNumber}</strong>
            </div>
          </div>
        </header>

        {detailLoading ? <RequestDetailSkeleton /> : (
          <>
            <div className="new-hrm-project-drawer__body">
              <section className="new-hrm-project-drawer__hero">
                <div className="new-hrm-project-drawer__badges">
                  <span>{request.requestType || "HRM Case"}</span>
                  <span className={`new-hrm-status new-hrm-status--${requestStatusClass(request.status)}`}>{request.status || "Pending"}</span>
                  <span className={`new-hrm-project-priority new-hrm-project-priority--${normalizeFilterKey(request.priority)}`}>{request.priority || "Medium"}</span>
                </div>
                <h2 id="request-detail-title">{request.subject}</h2>
                <p>Case {request.requestNumber} · Raised by {canonicalCasePersonName(request.requesterName, request.requesterEmail) || "Employee"} · Updated {request.updated || "recently"}</p>
                {canEditManagement && (
                  <div className="new-hrm-project-drawer__actions">
                    <button type="button" className="new-hrm-primary-button" onClick={handleManageSave} disabled={isSavingManagement || !hasManagementChanges}>
                      {isSavingManagement && <FiLoader className="new-hrm-inline-spinner" aria-hidden="true" />}
                      {isSavingManagement ? "Saving" : "Save changes"}
                    </button>
                  </div>
                )}
              </section>

              <div className="new-hrm-project-drawer__content">
                <main className="new-hrm-project-drawer__main">
                  <section className="new-hrm-project-drawer__section new-hrm-project-drawer__description" aria-label="Case description">
                    <div className="new-hrm-project-drawer__section-title">
                      <h3>Description</h3>
                      <small>Locked after submission</small>
                    </div>
                    <ProjectRichText value={request.description} emptyText="No description was added." />
                  </section>

                  <section className="new-hrm-project-drawer__section new-hrm-project-attachments new-hrm-request-drawer__files">
                    <div className="new-hrm-project-drawer__section-title">
                      <h3>Files</h3>
                      <div className="new-hrm-request-drawer__section-actions">
                        <small>{request.attachments?.length || 0} attached</small>
                        {request.canComment !== false && (
                          <>
                            <input
                              ref={attachmentInputRef}
                              type="file"
                              multiple
                              aria-label="Attach files to case"
                              onChange={handleAttachFiles}
                              disabled={isAttachingFiles}
                              hidden
                            />
                            <button type="button" className="new-hrm-secondary-button new-hrm-project-attachment-action" onClick={() => attachmentInputRef.current?.click()} disabled={isAttachingFiles || !canAttachFiles}>
                              {isAttachingFiles ? <FiLoader className="new-hrm-inline-spinner" aria-hidden="true" /> : <FiPaperclip aria-hidden="true" />}
                              {isAttachingFiles ? "Attaching" : "Attach"}
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                    {request.attachments?.length ? (
                      <div className="new-hrm-project-attachments__list">
                        {request.attachments.map((file, index) => {
                          const fileId = requestFileKey(file, index);
                          const isDownloading = downloadingFileId === fileId;
                          return (
                            <button key={fileId} type="button" onClick={() => handleDownloadFile(file, index)} disabled={Boolean(downloadingFileId)}>
                              {isDownloading ? <FiLoader className="new-hrm-inline-spinner" aria-hidden="true" /> : <FiFileText aria-hidden="true" />}
                              <span>{file.fileName || file.name || "Case file"}</span>
                              <small>{isDownloading ? "Opening" : file.fileType || file.updatedAt || "Download"}</small>
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="new-hrm-empty-state">No files attached yet.</p>
                    )}
                  </section>

                  <section className="new-hrm-project-drawer__section new-hrm-project-comments new-hrm-request-drawer__activity">
                    <div className="new-hrm-project-drawer__section-title">
                      <h3>Activity</h3>
                      <small>{request.commentCount || request.comments?.length || 0} update{(request.commentCount || request.comments?.length || 0) === 1 ? "" : "s"}</small>
                    </div>
                    <div className="new-hrm-project-comments__list">
                      {request.comments?.length ? (
                        request.comments.map((item) => (
                          <article key={item.id}>
                            <span className="new-hrm-avatar new-hrm-avatar--small">{getInitials(item.authorName)}</span>
                            <div>
                              <strong>{item.authorName || "AWC HRM"}</strong>
                              <small>{item.createdAt || "Recently"}</small>
                              <RequestCommentBody comment={item} people={directoryPeople} />
                              {item.attachments?.length > 0 && (
                                <div className="new-hrm-request-comment-files">
                                  {item.attachments.map((file, index) => {
                                    const fileId = requestFileKey(file, index);
                                    const isDownloading = downloadingFileId === fileId;
                                    return (
                                      <button key={fileId} type="button" onClick={() => handleDownloadFile(file, index)} disabled={Boolean(downloadingFileId)}>
                                        {isDownloading ? <FiLoader className="new-hrm-inline-spinner" aria-hidden="true" /> : <FiDownload aria-hidden="true" />}
                                        {file.fileName || file.name || "Attachment"}
                                      </button>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          </article>
                        ))
                      ) : (
                        <p className="new-hrm-empty-state">No activity yet.</p>
                      )}
                    </div>
                  </section>
                </main>

                <aside className="new-hrm-project-drawer__meta" aria-label="Case metadata">
                  {request.canChangeStatus ? (
                    <label className="new-hrm-project-drawer__field">
                      <span>Status</span>
                      <select value={status} onChange={(event) => setStatus(event.target.value)} disabled={isSavingManagement}>
                        {REQUEST_STATUS_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
                      </select>
                    </label>
                  ) : (
                    <div className="new-hrm-project-drawer__field">
                      <span>Status</span>
                      <strong>{request.status || "Pending"}</strong>
                    </div>
                  )}
                  {request.canReassign ? (
                    <label className="new-hrm-project-drawer__field">
                      <span>Owner</span>
                      <select aria-label="Owner" value={selectedOwnerAvailable ? ownerEmail : ""} onChange={(event) => setOwnerEmail(event.target.value)} disabled={isSavingManagement}>
                        <option value="">{hasUnavailableCurrentOwner ? "Select new owner" : "Request queue"}</option>
                        {REQUEST_OWNER_OPTIONS.map((option) => <option key={option.email} value={option.email}>{option.label}</option>)}
                      </select>
                      {hasUnavailableCurrentOwner && <small>Current owner: {resolvedOwner.label || currentOwnerEmail}</small>}
                    </label>
                  ) : (
                    <div className="new-hrm-project-drawer__field">
                      <span>Owner</span>
                      <strong>{resolvedOwner.label || "Request queue"}</strong>
                    </div>
                  )}
                  <div className="new-hrm-project-drawer__field">
                    <span>Type</span>
                    <strong>{request.requestType || "HRM Request"}</strong>
                  </div>
                  <div className="new-hrm-project-drawer__field">
                    <span>Priority</span>
                    <strong>{request.priority || "Medium"}</strong>
                  </div>
                  <div className="new-hrm-project-drawer__field">
                    <span>Requester</span>
                    <strong>{canonicalCasePersonName(request.requesterName, request.requesterEmail) || "Employee"}</strong>
                    {request.requesterEmail && <small>{request.requesterEmail}</small>}
                  </div>
                  <div className="new-hrm-project-drawer__field">
                    <span>Updated</span>
                    <strong>{request.updated || "Recently"}</strong>
                  </div>
                  <div className="new-hrm-project-drawer__field">
                    <span>Reference</span>
                    <strong>{request.requestNumber}</strong>
                  </div>
                </aside>
              </div>
            </div>

            {request.canComment !== false && (
              <footer className="new-hrm-project-drawer__composer new-hrm-request-drawer__composer">
                {!canAttachFiles && (
                  <div className="new-hrm-request-comment-warning">
                    Refresh the case list before adding comments or files. The Salesforce Case Id is missing.
                  </div>
                )}
                <CommentComposer
                  key={request.id}
                  onInlineImageUpload={onInlineImageUpload ? (file, options) => onInlineImageUpload(request.id, file, options) : undefined}
                  accept=""
                  assignees={directoryPeople}
                  attachmentAriaLabel="Attach case files"
                  compact
                  disabled={!canAttachFiles || isAttachingFiles}
                  emptyAttachmentBody="Added case attachment."
                  onSubmit={(body, selectedMentions, files, inlineFiles) => onSubmitComment?.(request.id, body, files, selectedMentions, ...(inlineFiles?.length ? [inlineFiles] : []))}
                  placeholder="Add a case update. Type @ to mention someone."
                />
              </footer>
            )}
          </>
        )}
      </aside>
    </div>
  );
};

const RequestsScreen = ({
  detailLoading = false,
  directoryPeople = [],
  loading = false,
  loadingLabel = "Loading requests",
  mode = "employee",
  onAction,
  onCloseRequest,
  onOpenRequest,
  onRequestFileDownload,
  onSubmitComment,
  onInlineImageUpload,
  onUpdateRequest,
  requests = [],
  selectedRequest,
}) => {
  const openRequests = requests.filter((item) => !isClosedStatus(item.status));
  const resolvedRequests = requests.filter((item) => isClosedStatus(item.status));
  const assignedRequests = requests.filter((item) => item.isAssignedWork);
  const isAssignedMode = mode === "assigned";
  const statusOptions = useMemo(() => {
    const statuses = new Map();
    [...REQUEST_STATUS_OPTIONS, ...requests.map((request) => request.status)].forEach((status) => {
      const value = normalizeStatusKey(status);
      if (!statuses.has(value)) {
        statuses.set(value, { label: String(status || "").trim() || "Unknown", value, raw: status });
      }
    });
    return Array.from(statuses.values());
  }, [requests]);
  const allStatusKeys = useMemo(() => statusOptions.map((option) => option.value), [statusOptions]);
  const defaultSelectedStatusKeys = useMemo(
    () => statusOptions.filter((option) => !isClosedStatus(option.raw)).map((option) => option.value),
    [statusOptions],
  );
  const [selectedStatusKeys, setSelectedStatusKeys] = useState(null);
  const activeStatusKeys = activeFilterKeys(selectedStatusKeys, defaultSelectedStatusKeys, allStatusKeys);
  const ownerOptions = useMemo(() => {
    const owners = new Map();
    REQUEST_OWNER_OPTIONS.forEach((owner) => {
      owners.set(normalizeOwnerKey(owner), {
        label: owner.label,
        value: normalizeOwnerKey(owner),
      });
    });
    requests.forEach((request) => {
      const owner = requestOwnerOption(request);
      const value = normalizeOwnerKey(owner);
      if (value && !owners.has(value)) {
        owners.set(value, {
          label: owner.label || "Request queue",
          value,
        });
      }
    });
    return Array.from(owners.values());
  }, [requests]);
  const defaultSelectedOwnerKeys = useMemo(() => ownerOptions.map((option) => option.value), [ownerOptions]);
  const [selectedOwnerKeys, setSelectedOwnerKeys] = useState(null);
  const activeOwnerKeys = activeFilterKeys(selectedOwnerKeys, defaultSelectedOwnerKeys);
  const requesterOptions = useMemo(() => {
    const requesters = new Map();
    requests.forEach((request) => {
      const requester = requestRequesterOption(request);
      const value = normalizeRequesterKey(requester);
      if (value && !requesters.has(value)) {
        requesters.set(value, {
          label: requester.label,
          value,
        });
      }
    });
    return Array.from(requesters.values()).sort((left, right) => left.label.localeCompare(right.label));
  }, [requests]);
  const defaultSelectedRequesterKeys = useMemo(
    () => requesterOptions.map((option) => option.value),
    [requesterOptions],
  );
  const [selectedRequesterKeys, setSelectedRequesterKeys] = useState(null);
  const activeRequesterKeys = activeFilterKeys(selectedRequesterKeys, defaultSelectedRequesterKeys);
  const [caseSearch, setCaseSearch] = useState("");
  const [openFilter, setOpenFilter] = useState("");
  const searchRef = useRef(null);

  const filteredRequests = useMemo(() => {
    const query = normalizeText(caseSearch);
    return requests.filter((request) => {
      const owner = requestOwnerOption(request);
      const requester = requestRequesterOption(request);
      const matchesFilters =
        activeStatusKeys.includes(normalizeStatusKey(request.status)) &&
        activeOwnerKeys.includes(normalizeOwnerKey(owner)) &&
        activeRequesterKeys.includes(normalizeRequesterKey(requester));
      if (!matchesFilters || !query) {
        return matchesFilters;
      }

      return [
        request.requestNumber,
        request.subject,
        request.description,
        request.requestType,
        request.priority,
        request.status,
        request.lastCommentBy,
        request.lastCommentPreview,
        owner.label,
        owner.email,
        requester.label,
        requester.email,
      ].some((value) => normalizeText(value).includes(query));
    });
  }, [activeOwnerKeys, activeRequesterKeys, activeStatusKeys, caseSearch, requests]);
  const statusSummary = filterSummary({
    allLabel: "All statuses",
    defaultLabel: "Open statuses",
    defaultValues: defaultSelectedStatusKeys,
    options: statusOptions,
    selectedValues: activeStatusKeys,
  });
  const ownerSummary = filterSummary({
    allLabel: "All owners",
    defaultLabel: "All owners",
    defaultValues: defaultSelectedOwnerKeys,
    options: ownerOptions,
    selectedValues: activeOwnerKeys,
  });
  const requesterSummary = filterSummary({
    allLabel: "All requesters",
    defaultLabel: "All requesters",
    defaultValues: defaultSelectedRequesterKeys,
    options: requesterOptions,
    selectedValues: activeRequesterKeys,
  });

  const toggleStatus = (statusKey) => {
    setSelectedStatusKeys((current) => toggleFilterKey(current, defaultSelectedStatusKeys, allStatusKeys, statusKey));
  };
  const toggleOwner = (ownerKey) => {
    setSelectedOwnerKeys((current) => toggleFilterKey(current, defaultSelectedOwnerKeys, defaultSelectedOwnerKeys, ownerKey));
  };
  const toggleRequester = (requesterKey) => {
    setSelectedRequesterKeys((current) => toggleFilterKey(current, defaultSelectedRequesterKeys, defaultSelectedRequesterKeys, requesterKey));
  };
  const resetFilters = () => {
    setSelectedStatusKeys(null);
    setSelectedOwnerKeys(null);
    setSelectedRequesterKeys(null);
    setCaseSearch("");
    setOpenFilter("");
    searchRef.current?.focus();
  };
  const filtersChanged = selectedStatusKeys !== null || selectedOwnerKeys !== null || selectedRequesterKeys !== null || caseSearch !== "";

  return (
    <section className="new-hrm-screen new-hrm-loader-host">
      <ComponentLoader active={loading && !selectedRequest} label={loadingLabel} />
      <ScreenHeader
        eyebrow="Salesforce"
        title={isAssignedMode ? "My Cases" : "Requests"}
        description={isAssignedMode ? "Work assigned HRM cases, update status, reassign ownership, add comments, and attach files." : "Track employee cases, ownership, and conversation history from one request queue."}
        actions={isAssignedMode ? null : (
          <button className="new-hrm-primary-button" type="button" onClick={() => onAction("hr")}>
            <FiPlus aria-hidden="true" />
            <span>New Request</span>
          </button>
        )}
      />
      <div className="new-hrm-screen-metrics">
        <ScreenMetric label={isAssignedMode ? "Open Cases" : "Open Requests"} value={openRequests.length} detail="Needs action" />
        <ScreenMetric label={isAssignedMode ? "Assigned Cases" : "Assigned To You"} value={isAssignedMode ? requests.length : assignedRequests.length} detail="Worker queue" />
        <ScreenMetric label="Resolved" value={resolvedRequests.length} detail="Closed requests" />
      </div>
      <section className="new-hrm-panel new-hrm-request-filter-panel">
        <div className="new-hrm-panel-title-row">
          <div>
            <h2>{isAssignedMode ? "Assigned Case Records" : "Request Records"}</h2>
            <p>{isAssignedMode ? "Only HRM case workers can see this queue." : "Open a request to add a case comment and continue the employee conversation."}</p>
          </div>
          <div className="new-hrm-request-filter-bar" aria-label="Case filters">
            <div className="new-hrm-request-search">
              <FiSearch aria-hidden="true" />
              <input
                ref={searchRef}
                aria-label="Search cases"
                onChange={(event) => setCaseSearch(event.target.value)}
                placeholder="Search cases"
                type="search"
                value={caseSearch}
              />
              {caseSearch && (
                <button
                  aria-label="Clear case search"
                  className="new-hrm-request-search__clear"
                  onClick={() => {
                    setCaseSearch("");
                    searchRef.current?.focus();
                  }}
                  title="Clear case search"
                  type="button"
                >
                  <FiX aria-hidden="true" />
                </button>
              )}
            </div>
            <MultiSelectDropdown
              filterId="status"
              isOpen={openFilter === "status"}
              label="Status"
              onOpenChange={setOpenFilter}
              onToggle={toggleStatus}
              options={statusOptions}
              selectedValues={activeStatusKeys}
              summary={statusSummary}
            />
            <MultiSelectDropdown
              filterId="owner"
              isOpen={openFilter === "owner"}
              label="Owner"
              onOpenChange={setOpenFilter}
              onToggle={toggleOwner}
              options={ownerOptions}
              selectedValues={activeOwnerKeys}
              summary={ownerSummary}
            />
            <MultiSelectDropdown
              filterId="requester"
              isOpen={openFilter === "requester"}
              label="Raised by"
              onOpenChange={setOpenFilter}
              onToggle={toggleRequester}
              options={requesterOptions}
              selectedValues={activeRequesterKeys}
              summary={requesterSummary}
            />
            <button className="new-hrm-secondary-button new-hrm-request-filter-reset" disabled={!filtersChanged} onClick={resetFilters} type="button">
              <FiRotateCcw aria-hidden="true" />
              <span>Reset filters</span>
            </button>
          </div>
        </div>
        <div className="new-hrm-data-table new-hrm-data-table--requests">
          <div className="new-hrm-data-table__head">
            <span>Request</span>
            <span>Subject</span>
            <span>Raised By</span>
            <span>Type</span>
            <span>Owner</span>
            <span>Status</span>
            <span>Activity</span>
          </div>
          {filteredRequests.length ? filteredRequests.map((request) => {
            const owner = requestOwnerOption(request);
            return (
            <button key={request.id} type="button" className="new-hrm-data-row" onClick={() => onOpenRequest(request)}>
              <strong data-label="Request">{request.requestNumber}</strong>
              <span data-label="Subject">
                {request.subject}
                {request.lastCommentPreview && <small>{request.lastCommentBy ? `${request.lastCommentBy}: ` : ""}{request.lastCommentPreview}</small>}
              </span>
              <span className="new-hrm-requester-cell" data-label="Raised By">
                <small className="new-hrm-requester-cell__label">Raised by</small>
                <strong>{canonicalCasePersonName(request.requesterName, request.requesterEmail) || "Employee"}</strong>
                {request.requesterEmail && <small>{request.requesterEmail}</small>}
              </span>
              <span data-label="Type">{request.requestType}</span>
              <span data-label="Owner">{owner.label || "Queue"}</span>
              <em data-label="Status" className={`new-hrm-status new-hrm-status--${requestStatusClass(request.status)}`}>
                {request.status}
              </em>
              <span data-label="Activity">{request.commentCount || 0} comments</span>
            </button>
          );
          }) : (
            <div className="new-hrm-request-filter-empty">
              <p className="new-hrm-empty-state" role="status">
                {requests.length ? "No cases match the selected filters." : "No cases available."}
              </p>
              {requests.length > 0 && (
                <button className="new-hrm-link-button" onClick={() => {
                  resetFilters();
                  setSelectedStatusKeys(allStatusKeys);
                }} type="button">Show all cases</button>
              )}
            </div>
          )}
        </div>
      </section>
      <RequestDetailDrawer
        detailLoading={detailLoading}
        directoryPeople={directoryPeople}
        onClose={onCloseRequest}
        onDownloadFile={onRequestFileDownload}
        onSubmitComment={onSubmitComment}
        onInlineImageUpload={onInlineImageUpload}
        onUpdateRequest={onUpdateRequest}
        request={selectedRequest}
      />
    </section>
  );
};

export default RequestsScreen;
